import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link2, Share2 } from "lucide-react";
import { FacebookIcon, MessengerIcon, WhatsAppIcon, ViberIcon } from "@/components/BrandIcons";
import { toast } from "sonner";
import { openShareWindow, copyToClipboard, nativeShare, canNativeShare, proShareMessage } from "@/lib/share";

// Focused social share menu for a Public Professional Profile URL (/pro/{slug}).
// Never shares /my-card or /q/{public_id}; never leaks private data.
export function ShareMenu({ open, onOpenChange, url, name, roleText }) {
  const text = proShareMessage({ name, position: roleText });
  const u = encodeURIComponent(url || "");
  const t = encodeURIComponent(text);

  const go = (href) => { if (!openShareWindow(href)) toast.error("Couldn't open the app. Try Copy Link."); };
  // Brand tiles render a WHITE glyph on a SOLID brand-color circle for instant recognition.
  const items = [
    { key: "facebook", label: "Facebook", Icon: FacebookIcon, brand: "#1877F2", solid: true, onClick: () => go(`https://www.facebook.com/sharer/sharer.php?u=${u}`) },
    { key: "messenger", label: "Messenger", Icon: MessengerIcon, brand: "#0084FF", solid: true, onClick: async () => {
        // URL-ONLY native share: `url` produces the rich professional preview; adding `text`
        // would create a redundant standalone message bubble in Messenger/iOS.
        if (canNativeShare()) { await nativeShare({ url }); }
        else if (!openShareWindow(`https://www.facebook.com/dialog/send?link=${u}&redirect_uri=${u}`)) { const ok = await copyToClipboard(url); toast(ok ? "Profile link copied." : "Copy failed"); }
      } },
    { key: "whatsapp", label: "WhatsApp", Icon: WhatsAppIcon, brand: "#25D366", solid: true, onClick: () => go(`https://wa.me/?text=${t}%20${u}`) },
    { key: "viber", label: "Viber", Icon: ViberIcon, brand: "#7360F2", solid: true, onClick: async () => {
        if (!openShareWindow(`viber://forward?text=${t}%20${u}`)) { const ok = await copyToClipboard(url); toast(ok ? "Profile link copied." : "Copy failed"); }
      } },
    { key: "copy", label: "Copy Link", Icon: Link2, brand: "#0F2C59", solid: false, onClick: async () => { const ok = await copyToClipboard(url); toast(ok ? "Profile link copied." : "Copy failed"); onOpenChange(false); } },
    { key: "more", label: "More…", Icon: Share2, brand: "#C79A3B", solid: false, onClick: async () => {
        // URL-ONLY native share (same rationale as Messenger).
        if (canNativeShare()) { await nativeShare({ url }); } else { const ok = await copyToClipboard(url); toast(ok ? "Profile link copied." : "Copy failed"); }
      } },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-2xl" data-testid="share-menu">
        <DialogHeader><DialogTitle className="text-[#0F2C59]">Share this profile</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Share this professional profile via Facebook, Messenger, WhatsApp, Viber, copy the link, or use your device share options.</DialogDescription>
        <div className="grid grid-cols-3 gap-3 pt-2">
          {items.map(({ key, label, Icon, brand, solid, onClick }) => (
            <button
              key={key}
              type="button"
              data-testid={`share-${key}`}
              onClick={onClick}
              className="flex flex-col items-center gap-2 rounded-xl border border-slate-200 py-4 hover:border-[#0F2C59] hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B]"
            >
              <span
                className="h-11 w-11 rounded-full flex items-center justify-center"
                style={solid ? { backgroundColor: brand, color: "#fff" } : { backgroundColor: `${brand}1A`, color: brand }}
              >
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-xs font-medium text-slate-700">{label}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-1 break-all" data-testid="share-url">{url}</p>
      </DialogContent>
    </Dialog>
  );
}
